import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Calendar, TrendingUp, Sprout, CheckCircle2, MapPin, ChevronDown, Box, Home } from 'lucide-react';
import { TrustBadge } from '@/components/TrustBadge';
import { cropImage } from '@/lib/cropImage';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  sage: '#E5EBDD', cream: '#FAF6EC', forest: '#1A4331', forestDeep: '#0F2D1F',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', gold: '#C9A85B', terracotta: '#C66B4A', lime: '#B4D330', sky: '#8AB4F8',
  text: '#2A3528', muted: '#5C665C', white: '#FFFFFF',
};
const CARD_SH = '0 6px 16px -2px rgba(26,67,49,0.12)';
const V = {
  h1: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 32, lineHeight: '38px', color: C.forest },
  h3: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 20, lineHeight: '26px', color: C.forest },
  title: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 18, lineHeight: '24px', color: C.forest },
  bodySemi: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 15, lineHeight: '22px', color: C.text },
  small: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: '19px', color: C.muted },
  label: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13, lineHeight: '18px', color: C.muted },
};

const TONE = {
  ready_now: { label: 'Ready now', bg: 'rgba(180,211,48,0.92)', fg: C.forest },
  ready: { label: 'Ready now', bg: 'rgba(180,211,48,0.92)', fg: C.forest },
  harvest_soon: { label: 'Soon', bg: 'rgba(201,168,91,0.95)', fg: C.forest },
  harvest_window: { label: 'In window', bg: 'rgba(201,168,91,0.95)', fg: C.forest },
  approaching: { label: 'Approaching', bg: 'rgba(138,180,248,0.92)', fg: C.forest },
  on_track: { label: 'On track', bg: 'rgba(107,159,74,0.92)', fg: C.white },
  growing: { label: 'Growing', bg: 'rgba(107,159,74,0.92)', fg: C.white },
  ahead: { label: 'Ahead', bg: 'rgba(138,180,248,0.92)', fg: C.forest },
  behind: { label: 'Behind', bg: 'rgba(198,107,74,0.92)', fg: C.white },
  stressed: { label: 'Stressed', bg: 'rgba(198,107,74,0.92)', fg: C.white },
};
const fmtDate = (d) => new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });

function HarvestPredictor({ user }) {
  const [predictions, setPredictions] = useState([]);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, w] = await Promise.all([
          axios.get(`${API}/harvests/predictions`, { withCredentials: true }),
          axios.get(`${API}/harvests/weather-predictions`, { withCredentials: true }).catch(() => ({ data: { available: false, predictions: [] } })),
        ]);
        setPredictions(p.data.predictions || []);
        setWeather(w.data);
      } catch (e) {
        console.error('Error fetching predictions:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const weatherAvailable = weather?.available && weather.predictions?.length > 0;
  const wmap = {};
  (weather?.predictions || []).forEach((r) => { wmap[r.planting_id] = r; });

  const effReadiness = (p) => wmap[p.planting_id]?.readiness || p.readiness;
  const readyNow = predictions.filter((p) => ['ready_now', 'ready'].includes(effReadiness(p)));
  const soon = predictions.filter((p) => ['harvest_soon', 'harvest_window', 'approaching'].includes(effReadiness(p)));
  const others = predictions.filter((p) => !['ready_now', 'ready', 'harvest_soon', 'harvest_window', 'approaching'].includes(effReadiness(p)));

  const Section = ({ icon: Icon, color, title, items, testid }) => (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${color}1f` }}><Icon className="w-5 h-5" style={{ color }} /></span>
        <h2 style={V.h3}>{title}</h2>
        <span className="rounded-full text-xs font-bold" style={{ background: C.leafTint || 'rgba(180,211,48,0.2)', color: C.forest, padding: '3px 10px', background: 'rgba(180,211,48,0.22)' }}>{items.length}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {items.map((p) => <HarvestCard key={p.planting_id} prediction={p} weather={wmap[p.planting_id]} />)}
      </div>
    </div>
  );

  return (
    <Layout user={user}>
      <div className="space-y-7">
        <div>
          <h1 style={V.h1}>Harvest Predictor</h1>
          <p style={V.small} className="mt-1 flex items-center gap-1" data-testid="harvest-subtitle">
            {weatherAvailable
              ? <><MapPin className="w-3.5 h-3.5" style={{ color: C.leaf }} /> Predicted from real weather {weather.nearest_town ? `near ${weather.nearest_town}` : (weather.location ? `at ${weather.location}` : 'at your garden')}{weather.climate_zone_name ? ` · ${weather.climate_zone_name}` : ''}</>
              : 'When your crops will be ready to harvest'}
          </p>
        </div>

        {predictions.length === 0 ? (
          <div className="relative overflow-hidden rounded-[22px] text-center" style={{ boxShadow: CARD_SH }}>
            <img src={cropImage('harvest')} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,45,31,0.55), rgba(15,45,31,0.9))' }} />
            <div className="relative px-6 py-14">
              <Sprout className="w-14 h-14 mx-auto mb-4" style={{ color: C.lime }} />
              <h3 style={{ ...V.h3, color: C.cream }}>No active crops yet</h3>
              <p style={{ ...V.small, color: 'rgba(250,246,236,0.85)' }} className="mt-1 mb-6">Plant some crops to see when they'll be ready to harvest</p>
              <a href="/crops" data-testid="harvest-browse-crops" className="inline-block font-semibold rounded-full px-8 py-3" style={{ background: C.lime, color: C.forest }}>Browse the Handbook</a>
            </div>
          </div>
        ) : (
          <>
            {readyNow.length > 0 && <Section icon={CheckCircle2} color={C.leafDeep} title="Ready to harvest" items={readyNow} testid="ready-now-section" />}
            {soon.length > 0 && <Section icon={TrendingUp} color={C.gold} title="Coming up next" items={soon} testid="harvest-soon-section" />}
            {others.length > 0 && <Section icon={Calendar} color={C.sky} title="Still growing" items={others} testid="growing-section" />}
          </>
        )}
      </div>
    </Layout>
  );
}

function HarvestCard({ prediction: p, weather: w }) {
  const [openWhy, setOpenWhy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const r = w?.badge || w?.readiness || p.readiness;
  const tone = TONE[r] || TONE.growing;
  const estDate = w?.predicted_date || p.expected_harvest_date;
  const hw = w?.harvest_window || p.harvest_window;
  const pct = Math.max(4, Math.min(100, p.progress_pct || 0));
  const img = cropImage(p.crop_name, p.category, p.image_url);
  const why = w?.why || p.why;
  const gdd = w?.gdd && w.gdd.heat_reliable && w.gdd.target ? w.gdd : null;
  const env = w?.environment || p.environment;
  const envLabel = w?.environment_label || p.environment_label || (env === 'indoor' ? 'Indoor' : 'Greenhouse');
  const EnvIcon = env === 'indoor' ? Box : Home;

  return (
    <div data-testid={`harvest-card-${p.planting_id}`} className="overflow-hidden" style={{ background: C.cream, borderRadius: 22, boxShadow: CARD_SH }}>
      <div className="relative" style={{ height: 130 }}>
        <img src={img} alt={p.crop_name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,45,31,0.1), rgba(15,45,31,0.4), rgba(15,45,31,0.92))' }} />
        <div className="absolute flex items-center justify-end gap-2" style={{ top: 12, left: 14, right: 14 }}>
          {env && env !== 'outdoor' && (
            <span className="inline-flex items-center gap-1 rounded-full" style={{ background: 'rgba(250,246,236,0.9)', padding: '4px 9px' }}>
              <EnvIcon className="w-3 h-3" style={{ color: C.forest }} />
              <span style={{ ...V.label, fontSize: 10, color: C.forest }}>{envLabel}</span>
            </span>
          )}
          <span className="rounded-full" style={{ background: tone.bg, padding: '4px 11px' }}>
            <span style={{ ...V.label, fontSize: 11, color: tone.fg }}>{tone.label}</span>
          </span>
        </div>
        <div className="absolute" style={{ left: 14, right: 14, bottom: 12 }}>
          <p className="truncate" style={{ ...V.title, color: C.cream }}>{p.crop_name}</p>
          {p.variety && <p className="truncate" style={{ ...V.small, color: 'rgba(250,246,236,0.85)' }}>{p.variety}</p>}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {p.message && <p style={{ ...V.bodySemi, marginBottom: 12 }}>{p.message}</p>}

        <div className="h-2 rounded-full overflow-hidden" style={{ background: '#E4E9DC' }}>
          <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: tone.bg, borderRadius: 999 }} />
        </div>
        <button onClick={() => setExpanded((o) => !o)} data-testid={`harvest-toggle-${p.planting_id}`}
          className="w-full flex justify-between items-center mt-1.5" style={V.small} aria-expanded={expanded}>
          <span>{pct.toFixed(0)}% grown</span>
          <span className="flex items-center gap-1">
            {p.days_remaining > 0 ? `${p.days_remaining} days left` : 'Ready!'}
            <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ color: C.leafDeep, transform: expanded ? 'rotate(180deg)' : 'none' }} />
          </span>
        </button>

        {expanded && (
          <>
            {gdd && (
              <div data-testid={`harvest-gdd-${p.planting_id}`} className="flex items-center justify-between rounded-2xl mt-2.5" style={{ background: C.white, padding: '8px 12px' }}>
                <span className="flex items-center gap-1.5" style={{ ...V.label, fontSize: 11 }}>
                  <TrendingUp className="w-3.5 h-3.5" style={{ color: C.leafDeep }} /> Heat units
                </span>
                <span style={{ ...V.bodySemi, fontSize: 13 }}>{gdd.accumulated} / {gdd.target}<span style={{ color: C.leafDeep }}> · {gdd.pct}%</span></span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-2xl" style={{ background: C.white, padding: 12 }}>
                <p style={{ ...V.label, fontSize: 10 }}>Days grown</p>
                <p style={V.bodySemi}>{p.days_grown}</p>
              </div>
              <div className="rounded-2xl" style={{ background: C.white, padding: 12 }}>
                <p style={{ ...V.label, fontSize: 10 }}>{hw ? 'Harvest window' : 'Expected harvest'}</p>
                {hw ? (
                  <>
                    <p style={V.bodySemi}>{fmtDate(hw.earliest)} – {fmtDate(hw.latest)}</p>
                    <p style={{ ...V.small, fontSize: 10, color: C.leafDeep }}>~{fmtDate(estDate)} likely</p>
                  </>
                ) : (
                  <p style={V.bodySemi}>{fmtDate(estDate)}</p>
                )}
              </div>
            </div>

            {why && (
              <>
                <button onClick={() => setOpenWhy((o) => !o)} data-testid={`harvest-why-${p.planting_id}`}
                  className="mt-3 flex items-center gap-1.5">
                  {p.confidence && <span className="rounded-full" style={{ background: 'rgba(107,159,74,0.14)', color: C.leafDeep, padding: '2px 9px', ...V.label, fontSize: 11 }}>{p.confidence}</span>}
                  <span style={{ ...V.label, fontSize: 12, color: C.leafDeep }}>{openWhy ? 'Hide' : 'Why this date?'}</span>
                  <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ color: C.leafDeep, transform: openWhy ? 'rotate(180deg)' : 'none' }} />
                </button>
                {openWhy && <p className="mt-2 rounded-2xl" style={{ ...V.small, background: C.white, padding: 12 }}>{why}</p>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default HarvestPredictor;
